import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PreselectedVoiceService } from "@/services/voice/PreselectedVoiceService";
import { PreselectedVoiceInput } from "@/types/voice/preselected-voice";
import { Voice } from "@/types/voice/voice";
import { useVoiceFavorites } from "./useVoiceFavorites";
import { useVoices } from "./useVoices";

const QUERY_KEY = ["preselected-voices"];

export const usePreselectedVoiceList = () =>
  useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => PreselectedVoiceService.list(),
    staleTime: 5 * 60 * 1000,
  });

export const useCreatePreselectedVoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PreselectedVoiceInput) => PreselectedVoiceService.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdatePreselectedVoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<PreselectedVoiceInput> }) =>
      PreselectedVoiceService.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeletePreselectedVoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => PreselectedVoiceService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

/**
 * The active, admin-curated voice list shown wherever owners select a supported voice.
 * The preselected records control both the order and customer-facing labels.
 */
export const usePreselectedVoices = () => {
  const { data: preselected = [], isLoading: preselectedLoading } = usePreselectedVoiceList();
  const { data: voicesResult, isLoading: voicesLoading } = useVoices();
  const allVoices = voicesResult?.voices;

  const voices = useMemo(
    () =>
      preselected
        .filter((row) => row.is_active)
        .map((row) => {
          const liveVoice = allVoices?.find((voice) => voice.id === row.voice_id);
          return {
            ...liveVoice,
            id: row.voice_id,
            name: row.label,
            description: row.description ?? liveVoice?.description,
            labels: { ...liveVoice?.labels, gender: row.gender },
          } as Voice;
        }),
    [preselected, allVoices],
  );

  return {
    voices,
    isLoading: preselectedLoading || voicesLoading,
  };
};

/**
 * Merges the admin-curated preselected voices with the user's favorites, de-duplicated by
 * voice_id. For preselected voices, our own table (label, description, gender) is the source
 * of truth — ElevenLabs' live record only fills in what we don't curate (preview_url,
 * verified_languages, accent/age/use_case), since its data can be missing or inconsistent
 * (e.g. some voices have no gender label at all).
 */
export const usePreselectedAndFavoriteVoices = () => {
  const { voices: preselectedVoices, isLoading: preselectedLoading } = usePreselectedVoices();
  const { data: favorites = [], isLoading: favoritesLoading } = useVoiceFavorites();

  const voices = useMemo(() => {
    const byId = new Map<string, Voice>();

    for (const voice of preselectedVoices) {
      byId.set(voice.id, voice);
    }

    for (const favorite of favorites) {
      if (favorite.voice_data?.id) {
        byId.set(favorite.voice_data.id, favorite.voice_data);
      }
    }

    return Array.from(byId.values());
  }, [preselectedVoices, favorites]);

  return {
    voices,
    isLoading: preselectedLoading || favoritesLoading,
  };
};
